'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowRightIcon, CalendarIcon, MapPinIcon, UsersIcon } from './LandingIcons';
import type { LandingViewer } from './landing-types';
type ContinueGroupsProps = {
  viewer: LandingViewer;
  focusedGroupId: string | null;
  newGroupHref: string;
  onOpenGroup: (groupId: string) => void;
};
export function ContinueGroups(t0) {
  const $ = _c(20);
  const {
    viewer,
    focusedGroupId,
    newGroupHref,
    onOpenGroup
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="inline-flex rounded-full border border-border-brand/50 bg-brand-primary-soft px-3 py-1 text-[11px] font-black uppercase tracking-[0.22em] text-brand-primary">Continue where you left off</div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== viewer.firstName) {
    t2 = <h2 className="mt-5 max-w-3xl text-4xl font-black tracking-[-0.055em] text-text-primary sm:text-6xl">Welcome back, {viewer.firstName}.</h2>;
    $[1] = viewer.firstName;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <p className="mt-4 max-w-2xl text-base leading-7 text-text-primary/55 sm:text-lg">Jump directly back into the itinerary, balances, stays, receipts, and shared media for a group.</p>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <div>{t1}{t2}{t3}</div>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <ArrowRightIcon className="size-4" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== newGroupHref) {
    t6 = <Link href={newGroupHref} className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full border border-border-brand/50 bg-brand-primary-soft px-5 py-3 text-sm font-black text-brand-primary transition hover:-translate-y-0.5 hover:bg-brand-primary hover:text-text-on-brand">New group{t5}</Link>;
    $[7] = newGroupHref;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4 || $[10] !== t6) {
    t7 = <div className="mb-7 flex flex-col gap-5 sm:mb-9 sm:flex-row sm:items-end sm:justify-between">{t4}{t6}</div>;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== focusedGroupId || $[13] !== newGroupHref || $[14] !== onOpenGroup || $[15] !== viewer.groups) {
    t8 = viewer.groups.length > 0 ? <div className="grid gap-4 lg:grid-cols-3">{viewer.groups.map(group => {
        const focused = focusedGroupId === group.id;
        return <Link key={group.id} id={`landing-group-${group.id}`} href={group.href} onClick={() => onOpenGroup(group.id)} {...focused ? {
          tabIndex: 0
        } : {}} className={`group relative block min-h-[290px] overflow-hidden rounded-[30px] border p-5 outline-none transition duration-300 focus-visible:ring-2 focus-visible:ring-text-primary/30 sm:p-6 ${focused ? "border-border-brand bg-brand-primary-soft/70 shadow-brand-glow" : "border-border bg-surface/80 hover:-translate-y-1 hover:border-border-brand/50 hover:bg-surface-raised"}`}>{group.coverImage ? <div className="absolute inset-0 bg-cover bg-center opacity-25 transition duration-500 group-hover:scale-105 group-hover:opacity-35" style={{
            backgroundImage: `linear-gradient(to top, var(--color-bg) 8%, transparent 72%), url(${group.coverImage})`
          }} /> : <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_12%,var(--color-brand-secondary-soft),transparent_34%),linear-gradient(145deg,var(--color-brand-primary-soft),var(--color-surface)_52%,var(--color-bg))] opacity-95 transition duration-500 group-hover:opacity-100" />}<div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-bg to-transparent" /><div className="relative flex min-h-[242px] flex-col"><div className="flex items-center justify-between gap-3"><span className="rounded-full border border-border bg-bg/55 px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] text-text-primary/70 backdrop-blur-md">{group.status ?? "Planning"}</span><span className="size-2.5 rounded-full bg-brand-primary shadow-brand-glow" /></div><div className="mt-auto"><h3 className="text-2xl font-black tracking-[-0.04em] text-text-primary sm:text-3xl">{group.name}</h3><div className="mt-3 flex items-center gap-2 text-sm text-text-primary/55"><MapPinIcon className="size-4 shrink-0" /><span>{group.destination ?? "Destination not set"}</span></div><div className="mt-5 flex items-center justify-between border-t border-border pt-4 text-xs font-bold text-text-primary/45"><span className="inline-flex items-center gap-1.5"><CalendarIcon className="size-4" />{group.dateLabel ?? "Dates open"}</span><span className="inline-flex items-center gap-1.5"><UsersIcon className="size-4" />{group.memberCount ?? 1}</span></div><div className="mt-4 inline-flex items-center gap-2 text-sm font-black text-text-primary/70 transition group-hover:text-brand-primary">Open group<ArrowRightIcon className="size-4 transition group-hover:translate-x-1" /></div></div></div></Link>;
      })}</div> : <div className="rounded-[32px] border border-dashed border-border bg-surface/55 px-6 py-12 text-center backdrop-blur-xl"><UsersIcon className="mx-auto size-10 text-brand-primary" /><h3 className="mt-5 text-2xl font-black text-text-primary">No groups yet</h3><p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-text-primary/50">Create the first group, invite the crew, and start turning ideas into a real trip.</p><Link href={newGroupHref} className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-primary px-5 py-3 text-sm font-black text-text-on-brand transition hover:bg-brand-primary-hover">Create a group<ArrowRightIcon className="size-4" /></Link></div>;
    $[12] = focusedGroupId;
    $[13] = newGroupHref;
    $[14] = onOpenGroup;
    $[15] = viewer.groups;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className="w-full">{t7}{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}